import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import { useBuyBlock } from "../hooks/useCapacityBlocks";

const BLOCK_OPTIONS = [
  { kind: "STUDENTS", label: "+50 estudiantes activos", price: "$25.000/mes" },
  { kind: "TEACHERS", label: "+5 docentes", price: "$20.000/mes" },
  { kind: "HEADQUARTERS", label: "+1 sede", price: "$49.000/mes" },
];

const HEADING_ID = "buy-block-modal-title";

/**
 * BuyBlockModal — selector de bloque de capacidad + POST + toast de éxito.
 * Focus trap garantizado vía autoFocus en el primer radio + onEntered callback.
 *
 * Props:
 *   show    {boolean}
 *   onHide  {function}
 */
function BuyBlockModal({ show, onHide, initialKind = "STUDENTS" }) {
  const [selectedKind, setSelectedKind] = useState(initialKind);
  const firstRadioRef = useRef(null);
  const buyBlock = useBuyBlock();

  // Reset selection (al bloque solicitado) y error cada vez que se abre
  useEffect(() => {
    if (show) {
      setSelectedKind(initialKind);
      buyBlock.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialKind]);

  function handleSubmit(e) {
    e.preventDefault();
    buyBlock.mutate(selectedKind, {
      onSuccess: () => {
        toast.success(
          "Bloque solicitado. Se activará y ampliará tu límite cuando confirmemos tu pago."
        );
        onHide();
      },
    });
  }

  function handleEntered() {
    firstRadioRef.current?.focus();
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      aria-labelledby={HEADING_ID}
      aria-modal="true"
      onEntered={handleEntered}
    >
      <Modal.Header closeButton>
        <Modal.Title id={HEADING_ID}>Comprar bloque de capacidad</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {buyBlock.isError && (
          <Alert variant="danger" className="mb-3">
            {buyBlock.error?.response?.data?.message ||
              buyBlock.error?.response?.data?.detail ||
              "Error al contratar el bloque. Inténtalo de nuevo."}
          </Alert>
        )}

        <Form id="buy-block-form" onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Label className="fw-medium mb-2">Selecciona el bloque</Form.Label>
            {BLOCK_OPTIONS.map((opt, i) => (
              <Form.Check
                key={opt.kind}
                ref={i === 0 ? firstRadioRef : null}
                type="radio"
                id={`block-kind-${opt.kind}`}
                name="block-kind"
                value={opt.kind}
                checked={selectedKind === opt.kind}
                onChange={() => setSelectedKind(opt.kind)}
                className="mb-2"
                label={
                  <span>
                    <strong>{opt.label}</strong>{" "}
                    <span className="text-muted small">— {opt.price}</span>
                  </span>
                }
              />
            ))}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          aria-label="Cancelar compra de bloque"
          disabled={buyBlock.isPending}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="buy-block-form"
          disabled={buyBlock.isPending}
          aria-label="Confirmar y contratar el bloque seleccionado"
        >
          {buyBlock.isPending ? (
            <>
              <Spinner size="sm" className="me-1" aria-hidden="true" />
              Procesando...
            </>
          ) : (
            "Contratar bloque"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export { BuyBlockModal };
